import { Component, Input, OnInit } from '@angular/core';
import { ModalController } from '@ionic/angular';
import Article from '../../../smoothr-web-app-core/models/Article';
import Venue from '../../../smoothr-web-app-core/models/Venue';
import Order from '../../../smoothr-web-app-core/models/Order';
import { TranslateService } from '@ngx-translate/core';
import { fieldDepositOfArticle, getBasePrice, getPrice, numberToCurrency } from '../../../smoothr-web-app-core/utils/utils';
import { OrderType } from '../../../smoothr-web-app-core/enums/OrderType';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { TracifyService } from '../../services/tracify.service';

@Component({
	selector: 'app-product-info-modal',
	templateUrl: './product-info-modal.component.html',
	styleUrls: ['./product-info-modal.component.scss'],
})
export class ProductInfoModalComponent implements OnInit {
	@Input() article: Article;
	@Input() venue: Venue;
	@Input() order: Order;
	@Input() currency: string;
	@Input() preorderType: PreorderType;

	constructor(private modalController: ModalController, private translate: TranslateService, private tracifyService: TracifyService) {}

	ngOnInit() {
		// Track product view when modal opens
		this.tracifyService.trackProductView();
	}

	get priceText(): string {
		if (!this.article || !this.currency) return '';
		// The configured price includes the deposit meta field; show the base price.
		const price = getPrice(this.article, OrderType.PREORDER, this.preorderType, null) - fieldDepositOfArticle(this.article);
		return numberToCurrency(price, this.currency);
	}

	static async show(modalController: ModalController, article: Article, venue: Venue, order: Order): Promise<any> {
		const modal = await modalController.create({
			component: ProductInfoModalComponent,
			componentProps: {
				article,
				venue,
				order,
				currency: venue?.currency,
				preorderType: order?.preorder?.type,
			},
			cssClass: 'item-modal',
			mode: 'ios',
			backdropDismiss: true,
		});

		await modal.present();
		return modal.onDidDismiss();
	}

	get basePrice(): string {
		// todo: this is a duplicate
		if (!this.article?.measurement || !this.currency || !this.preorderType) {
			return '';
		}

		const measurement = this.article.measurement;
		if (!measurement.refAmount || !measurement.refUnit || !measurement.amount) {
			return '';
		}

		if (measurement.refAmount == 100 && measurement.refUnit === 'g') {
			return '1 kg = ' + numberToCurrency(getBasePrice(this.article, OrderType.PREORDER, this.preorderType) * 10, this.currency);
		}
		if (measurement.refAmount == 100 && measurement.refUnit === 'ml') {
			return '1 l = ' + numberToCurrency(getBasePrice(this.article, OrderType.PREORDER, this.preorderType) * 10, this.currency);
		}

		return (
			measurement.refAmount +
			' ' +
			measurement.refUnit +
			' = ' +
			numberToCurrency(getBasePrice(this.article, OrderType.PREORDER, this.preorderType), this.currency)
		);
	}

	get measurmentText(): string {
		const fieldDeposit = fieldDepositOfArticle(this.article);
		if (fieldDeposit > 0) {
			// The shown price has the deposit taken off, so it is charged on top.
			return (
				this.translate.instant('pfand_text.zzgl') +
				numberToCurrency(fieldDeposit, this.currency) +
				this.translate.instant('pfand_text.pfand')
			);
		}

		const pfandValue = this.checkPfandTag(this.article);
		if (!pfandValue) {
			return '';
		}

		const pfandPrice = this.getPricePfandValue(pfandValue);
		return (
			this.translate.instant('pfand_text.zzgl') +
			numberToCurrency(pfandPrice, this.currency) +
			this.translate.instant('pfand_text.pfand')
		);
	}

	checkPfandTag(article: Article): string | null {
		// todo: this is a duplicate
		if (!article?.tags || article.tags.length === 0) {
			return null;
		}

		const deposit25 = article.tags.find(it => it.identifier === 'deposit25');
		const deposit15 = article.tags.find(it => it.identifier === 'deposit15');
		const deposit8 = article.tags.find(it => it.identifier === 'deposit08');
		const deposit28 = article.tags.find(it => it.identifier === 'deposit28');

		if (deposit25) {
			return 'deposit25';
		}
		if (deposit15) {
			return 'deposit15';
		}
		if (deposit8) {
			return 'deposit08';
		}
		if (deposit28) {
			return 'deposit28';
		}

		return null;
	}

	getPricePfandValue(depositType: string): number {
		// todo: this is a duplicate
		if (depositType === 'deposit25') {
			return 0.25;
		}
		if (depositType === 'deposit15') {
			return 0.15;
		}
		if (depositType === 'deposit08') {
			return 0.08;
		}
		if (depositType === 'deposit28') {
			return 0.28;
		}
		return 0;
	}

	dismiss() {
		this.modalController.dismiss();
	}
}
